/**
 * Filters for pairs.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { SearchInput } from '@/components/forms/SearchInput';
import { Select } from '@/components/forms/Select';
import {
  PAIR_INPERSON_FILTER_LABELS as FILTER_LABELS,
  PAIR_TYPE_CONFIG,
  DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY,
  type PairInPersonFilters as Filters,
} from '@/components/pairs/pair-configs';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useSubjectsSelect } from '@/hooks/useSubjectsSelect';
import {
  formatPairFilterDateToString,
  isPairFilterVisible,
  parsePairFilterDateInput,
} from './pair-table-utils';

/** Props for {@link PairFiltersContent}. */
interface PairFiltersContentProps {
  filters: Filters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleType: (type: string) => void;
  onSetTutorSearch: (search: string | undefined) => void;
  onSetStudentSearch: (search: string | undefined) => void;
  onSetDateRange: (range: Filters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetSubject: (id: string | undefined) => void;
}

/** Pair-type chips (manual / auto / …). */
const TypeFilter: React.FC<{
  filters: Filters;
  onToggleType: (type: string) => void;
}> = ({ filters, onToggleType }) => (
  <div className="filter-group">
    <span className="filter-group-label">{FILTER_LABELS['filter-type']}</span>
    <div className="filter-options">
      {Object.entries(PAIR_TYPE_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.types.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
            onClick={() => onToggleType(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Free-text tutor search (name, email, phone). */
const TutorSearchFilter: React.FC<{
  filters: Filters;
  onSetTutorSearch: (search: string | undefined) => void;
}> = ({ filters, onSetTutorSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">{FILTER_LABELS['filter-tutor-search']}</span>
    <SearchInput
      size="sm"
      shortcut={false}
      placeholder="Nom, email ou téléphone..."
      className="min-w-64"
      value={filters.tutorSearch ?? ''}
      onChange={(e) => onSetTutorSearch(e.target.value || undefined)}
      onClear={() => onSetTutorSearch(undefined)}
    />
  </div>
);

/** Free-text student search (name, email, phone). */
const StudentSearchFilter: React.FC<{
  filters: Filters;
  onSetStudentSearch: (search: string | undefined) => void;
}> = ({ filters, onSetStudentSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">{FILTER_LABELS['filter-student-search']}</span>
    <SearchInput
      size="sm"
      shortcut={false}
      placeholder="Nom, email ou téléphone..."
      className="min-w-64"
      value={filters.studentSearch ?? ''}
      onChange={(e) => onSetStudentSearch(e.target.value || undefined)}
      onClear={() => onSetStudentSearch(undefined)}
    />
  </div>
);

/** Preset period control; picking a preset clears the custom date range. */
const PeriodFilter: React.FC<{
  filters: Filters;
  onSetDateRange: (range: Filters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">{FILTER_LABELS['filter-period']}</span>
    <SegmentedControl
      size="sm"
      value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
      onChange={(v) => {
        onSetDateRange(v as Filters['dateRange']);
        onSetCustomDateRange(undefined, undefined);
      }}
      options={[...DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY]}
    />
  </div>
);

/** Custom from/to range; setting a date resets the period preset to `all`. */
const DateRangeFilter: React.FC<{
  filters: Filters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetDateRange: (range: Filters['dateRange']) => void;
}> = ({ filters, onSetCustomDateRange, onSetDateRange }) => {
  const fromStr = formatPairFilterDateToString(filters.customDateFrom);
  const toStr = formatPairFilterDateToString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    onSetCustomDateRange(parsePairFilterDateInput(val, false), filters.customDateTo);
    onSetDateRange('all');
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    onSetCustomDateRange(filters.customDateFrom, parsePairFilterDateInput(val, true));
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">{FILTER_LABELS['filter-date-range']}</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** Establishment autocomplete (prospection-validated list). */
const EstablishmentFilter: React.FC<{
  filters: Filters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  return (
    <div className="filter-group">
      <span className="filter-group-label">{FILTER_LABELS['filter-establishment']}</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          inputSize="sm"
          options={options}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Subject select; fetch is skipped until this filter is visible. */
const SubjectFilter: React.FC<{
  filters: Filters;
  onSetSubject: (id: string | undefined) => void;
  enabled: boolean;
}> = ({ filters, onSetSubject, enabled }) => {
  const { options, isLoading, onLoadMore, hasMore } = useSubjectsSelect({ enabled });

  const selectOptions = [{ value: '', label: 'Toutes' }, ...options];

  return (
    <div className="filter-group">
      <span className="filter-group-label">{FILTER_LABELS['filter-subject']}</span>
      <Select
        options={selectOptions}
        value={filters.subjectId ?? ''}
        onChange={(val) => onSetSubject(val || undefined)}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        size="sm"
        className="min-w-36"
      />
    </div>
  );
};

/** In-person pair filter ribbon; each group is gated by allowed ∩ visible ids. */
export const PairFiltersContent: React.FC<PairFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleType,
  onSetTutorSearch,
  onSetStudentSearch,
  onSetDateRange,
  onSetCustomDateRange,
  onSetEstablishment,
  onSetSubject,
}) => {
  const show = (id: string) => isPairFilterVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-type') && <TypeFilter filters={filters} onToggleType={onToggleType} />}

      {show('filter-tutor-search') && (
        <TutorSearchFilter filters={filters} onSetTutorSearch={onSetTutorSearch} />
      )}

      {show('filter-student-search') && (
        <StudentSearchFilter filters={filters} onSetStudentSearch={onSetStudentSearch} />
      )}

      {show('filter-period') && (
        <PeriodFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-date-range') && (
        <DateRangeFilter
          filters={filters}
          onSetCustomDateRange={onSetCustomDateRange}
          onSetDateRange={onSetDateRange}
        />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}

      {show('filter-subject') && (
        <SubjectFilter filters={filters} onSetSubject={onSetSubject} enabled={true} />
      )}
    </div>
  );
};
